import { hits, scale, strokesToRects, tapToRect, type MaskPoint, type MaskRect } from '@innspel/core';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { Ikon } from '../Ikon.tsx';
import type { Fangst } from '../skjermbilde/typer.ts';
import { Btn, H, Hint } from './primitiver.tsx';
import { tekst } from './tekster.ts';

type Verktoy = 'felt' | 'pensel';

/**
 * 2f — skjermbildet, forhåndsvist FØR noe sendes (K4). Tre likestilte
 * maskeringsveier (DD-40): «Masker felt» (ett klikk legger en boks), «Tegn
 * over» (dra) og «Masker all tekst» (tekstlinjene dekkes; for en fil pikseleres
 * bildet). Private felter (DD-39) ligger der før innsenderen ser bildet, merket
 * «Maskert av appen», og kan ikke fjernes. Egne masker angres med ett klikk.
 *
 * Presisert 2026-09-14 (C2, C11d): maskene bor i `Widget`, fordi «Legg ved» står
 * i dialogens fot. «Angre siste» finnes som KNAPP — å treffe en maske med musa
 * er en motorisk oppgave, og angrefunksjonen kan ikke være den eneste veien
 * tilbake (samme grunn som DD-40 gir pensel OG knapp).
 *
 * Alle masker holdes i BILDETS piksler; lagene tegnes som prosent av flaten,
 * så de følger bildet uansett hvor stort det vises. Komposisjonen skjer i
 * `compose` — det er komposittet som lastes opp, aldri rå-bitmapen.
 */
export function Skjermbilde({
  fangst,
  komponerer,
  notat,
  egne,
  onEgne,
  allTekst,
  onAllTekst,
  onFjern,
}: {
  fangst: Fangst;
  komponerer: boolean;
  notat?: string;
  egne: MaskRect[];
  onEgne: (m: MaskRect[]) => void;
  allTekst: boolean;
  onAllTekst: (v: boolean) => void;
  onFjern: () => void;
}) {
  const [verktoy, setVerktoy] = useState<Verktoy>('pensel');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const flateRef = useRef<HTMLDivElement | null>(null);
  const strok = useRef<MaskPoint[]>([]);
  const mål = useRef(lesMål());

  // Rå-bitmapen tegnes til forhåndsvisningen. jsdom har ingen canvas-kontekst; da
  // står flaten tom, og det er alt som skjer.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = fangst.width;
    c.height = fangst.height;
    const ctx = c.getContext('2d');
    if (ctx) ctx.drawImage(fangst.bilde, 0, 0);
  }, [fangst]);

  const bilde = { width: fangst.width, height: fangst.height };

  /** Pekerens posisjon i bildets piksler. */
  const punkt = (e: PointerEvent): MaskPoint | null => {
    const el = flateRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return null;
    return scale({ x: e.clientX - r.left, y: e.clientY - r.top, width: 0, height: 0 }, { width: r.width, height: r.height }, bilde);
  };

  /** Forholdet bildepiksler per CSS-piksel — så boks og pensel får samme størrelse på skjermen uansett bilde. */
  const ratio = (): number => {
    const r = flateRef.current?.getBoundingClientRect();
    return r && r.width > 0 ? fangst.width / r.width : 1;
  };

  const onDown = (e: PointerEvent) => {
    if (komponerer) return;
    const p = punkt(e);
    if (!p) return;
    // Ett klikk på en egen maske angrer den (wireframe 2f). Private felter rører den aldri.
    const treff = egne.findIndex((r) => hits(p, r));
    if (treff >= 0) {
      onEgne(egne.filter((_, i) => i !== treff));
      return;
    }
    if (verktoy === 'felt') {
      const k = ratio();
      const r = tapToRect(p, { width: 4 * mål.current.touch * k, height: mål.current.touch * k }, bilde);
      if (r) onEgne([...egne, r]);
      return;
    }
    strok.current = [p];
    flateRef.current?.setPointerCapture(e.pointerId);
  };

  const onMove = (e: PointerEvent) => {
    if (verktoy !== 'pensel' || strok.current.length === 0) return;
    const p = punkt(e);
    if (p) strok.current.push(p);
  };

  const onUp = () => {
    if (verktoy !== 'pensel' || strok.current.length === 0) return;
    const rekter = strokesToRects(strok.current, mål.current.brush * ratio(), bilde);
    strok.current = [];
    if (rekter.length) onEgne([...egne, ...rekter]);
  };

  const prosent = (r: MaskRect) => ({
    left: `${(r.x / bilde.width) * 100}%`,
    top: `${(r.y / bilde.height) * 100}%`,
    width: `${(r.width / bilde.width) * 100}%`,
    height: `${(r.height / bilde.height) * 100}%`,
  });

  const knapp = (id: Verktoy | 'allTekst', ikon: string, label: string) => {
    const valgt = id === 'allTekst' ? allTekst : verktoy === id;
    return (
      <button
        key={id}
        type="button"
        className="innspel-verktoy"
        data-testid={`verktoy-${id}`}
        aria-pressed={valgt}
        onClick={() => (id === 'allTekst' ? onAllTekst(!allTekst) : setVerktoy(id))}
      >
        <Ikon navn={ikon} />
        {label}
      </button>
    );
  };

  const a11y = tekst.skjermbildeA11y(fangst.automasker.length, egne.length, allTekst, fangst.kilde === 'fil');

  return (
    <div className="innspel-stabel">
      <H sub={fangst.kilde === 'fil' ? tekst.skjermbildeUnderFil : tekst.skjermbildeUnder}>{tekst.skjermbildeTittel}</H>
      {notat ? (
        <div data-testid="bilde-notat">
          <Hint>{notat}</Hint>
        </div>
      ) : null}

      <div role="toolbar" aria-label={tekst.maskering} className="innspel-rad">
        {knapp('felt', 'scan-outline', tekst.maskerFelt)}
        {knapp('pensel', 'brush-outline', tekst.tegnOver)}
        {knapp('allTekst', 'grid-outline', tekst.maskerAllTekst)}
      </div>

      <div className="innspel-forhandsvisning-ramme" data-testid="skjermbilde-ramme">
        <div
          ref={flateRef}
          className="innspel-forhandsvisning"
          role="img"
          aria-label={a11y}
          data-testid="forhandsvisning"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          <canvas ref={canvasRef} data-testid="skjermbilde-bilde" style={allTekst && !fangst.tekstRekter?.length ? { filter: 'blur(var(--innspel-space-md))' } : undefined} />
          {allTekst
            ? (fangst.tekstRekter ?? []).map((r, i) => <div key={`tekst-${i}`} className="innspel-maske" data-art="auto" style={prosent(r)} />)
            : null}
          {fangst.automasker.map((r, i) => (
            <div key={`auto-${i}`} className="innspel-maske" data-art="auto" data-testid={`automask-${i}`} style={prosent(r)}>
              {!komponerer ? (
                <span className="innspel-maske-etikett">
                  <Ikon navn="lock-closed-outline" /> {tekst.maskertAvAppen}
                </span>
              ) : null}
            </div>
          ))}
          {egne.map((r, i) => (
            <div key={`egen-${i}`} className="innspel-maske" data-art="egen" data-testid={`maske-${i}`} style={prosent(r)} />
          ))}
        </div>
      </div>

      {fangst.automasker.length ? (
        <Hint>
          <Ikon navn="lock-closed-outline" /> {tekst.maskertAvAppenHint}
        </Hint>
      ) : null}
      {allTekst ? <Hint>{tekst.allTekstHint}</Hint> : null}

      <div className="innspel-rad" data-gap="sm">
        <Btn
          grow
          variant="secondary"
          testId="angre-siste"
          ikon="arrow-undo-outline"
          label={tekst.angreSiste}
          ariaLabel={tekst.angreSisteA11y}
          disabled={komponerer || egne.length === 0}
          onClick={() => onEgne(egne.slice(0, -1))}
        />
        <Btn grow variant="danger" testId="fjern-bildet" ikon="trash-outline" label={tekst.fjernBildet} onClick={onFjern} disabled={komponerer} />
      </div>
      {egne.length ? <Hint>{tekst.dittStrok}</Hint> : null}
    </div>
  );
}

/** Trykkflate og penselbredde fra tokens.css. Reserven er tallene i theme.ts, for et dokument uten stilarket. */
function lesMål(): { touch: number; brush: number } {
  if (typeof getComputedStyle !== 'function') return { touch: 48, brush: 28 };
  const stil = getComputedStyle(document.documentElement);
  const tall = (navn: string, reserve: number) => {
    const v = parseFloat(stil.getPropertyValue(navn));
    return Number.isFinite(v) && v > 0 ? v : reserve;
  };
  return { touch: tall('--innspel-layout-touch', 48), brush: tall('--innspel-screen-brush-width', 28) };
}
